import { defineStore } from 'pinia'
import axios from '@/api/axios'
import { useAuthStore } from './index'
import { useSwal } from '@/composables'
import { extractClapError } from '@/helpers/errorHelper'

type State = {
  currentUser: User | null
  allUsers: User[]
  filtered: User[]
  count: number
  totalUsersMedia: any
}

export const useUsersStore = defineStore('users', {
  state: (): State => ({
    currentUser: null,
    allUsers: [],
    filtered: [],
    count: 0,
    totalUsersMedia: null,
  }),

  getters: {
    userById: state => (id: string) => state.allUsers.find(u => u._id === id),
    users: state => state.allUsers,
    user: state => state.currentUser,
    userFiltered: state => state.filtered,
    usersCount: state => state.count,
    totalMedia: state => state.totalUsersMedia?.[0],
  },

  actions: {
    setUserCount(count: number) {
      this.count = count
    },
    setTotalMedia(payload: any) {
      this.totalUsersMedia = payload
    },
    setUsers(payload: any[]) {
      this.allUsers = payload
      this.filtered = [...payload]
    },
    setUser(payload: any) {
      this.currentUser = payload
    },
    filterUsers({ key, val }: { key: keyof User; val: any }) {
      this.filtered = !val
        ? [...this.allUsers]
        : this.allUsers.filter(f => f[key] === val)
    },
    storeUser(payload: any) {
      this.allUsers.push(payload)
    },
    destroyUser(id: string) {
      this.allUsers = this.allUsers.filter(u => u._id !== id)
    },
    updateUser(payload: any) {
      const id = String(payload?._id || '')
      this.allUsers = this.allUsers.map(user =>
        String(user._id) === id ? { ...user, ...payload } : user
      )
    },

    async indexUsers({
      page = 1,
      perPage = 25,
      search = '',
      sortBy = '',
      sortDesc = '',
      affiliateRef = '',
      partnerId = '',
    } = {}) {
      try {
        const params = new URLSearchParams({
          page: String(page),
          perPage: String(perPage),
          searchText: search,
          sortBy,
          sortDesc,
        })
        if (affiliateRef) {
          params.set('affiliateRef', affiliateRef)
        }
        if (partnerId) {
          params.set('partnerId', partnerId)
        }
        const { data } = await axios.get(`/user?${params.toString()}`)
        this.setUsers(data.users)
        this.setUserCount(data.count)
        this.setTotalMedia(data.totalMedia)
      } catch (e) {
        console.error(e)

        useSwal().timeoutSwal({
          icon: 'error',
          title: extractClapError(e as ClapError),
        })
      }
    },

    async getUserById(id: string) {
      try {
        const { data } = await axios.get(`/user/${id}`)
        this.setUser(data)
      } catch (e) {
        console.error(e)

        useSwal().timeoutSwal({
          icon: 'error',
          title: extractClapError(e as ClapError),
        })
      }
    },

    async createUser(payload: any) {
      try {
        const { data } = await axios.post('/auth/register', payload)
        this.storeUser(data)
      } catch (e) {
        console.error(e)

        useSwal().timeoutSwal({
          icon: 'error',
          title: extractClapError(e as ClapError),
        })
      }
    },

    async deleteUser(id: string) {
      try {
        await axios.delete(`/user/${id}`)
        this.destroyUser(id)
      } catch (e) {
        console.error(e)

        useSwal().timeoutSwal({
          icon: 'error',
          title: extractClapError(e as ClapError),
        })
      }
    },

    async updateUserRemote(payload: any) {
      try {
        await axios.put(`/user/${payload._id}`, payload)
        this.updateUser(payload)

        const authStore = useAuthStore()
        // Only refresh the signed-in profile when editing yourself
        if (String(authStore.uid) === String(payload._id)) {
          authStore.userDetails = { ...authStore.userDetails, ...payload }
        }
        return true
      } catch (e) {
        console.error(e)

        useSwal().timeoutSwal({
          icon: 'error',
          title: extractClapError(e as ClapError),
        })
        return false
      }
    },
  },
})
